import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import { useTranslation } from 'react-i18next';
import FadeInSection from '@/components/base/FadeInSection';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ContentMentions from './components/ContentMentions';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'mentions-ld') el.remove();
  });
}

export default function MentionsLegalesPage() {
  const { t } = useTranslation('mentionslegales');

  usePageSEO({
    title: "Mentions légales | Couvreur-Lévis.com",
    description: "Mentions légales de Couvreur-Lévis.com, plateforme de mise en relation entre clients et couvreurs partenaires à Lévis, Québec.",
    urlPath: "/mentions-legales",
    keywords: "mentions légales, Couvreur-Lévis.com, légal, Québec",
    ogImage: '/og/og-mentions-legales.jpg',
    ogImageAlt: 'Mentions légales - Couvreur-Lévis.com',
    jsonLdSchema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
        { '@type': 'ListItem', position: 2, name: 'Mentions légales', item: 'https://couvreur-levis.com/mentions-legales' },
      ],
    },
  });

  useEffect(() => {
    document.title = "Mentions légales | Couvreur-Lévis.com";
    setMeta('description', "Mentions légales de Couvreur-Lévis.com, plateforme de mise en relation entre clients et couvreurs partenaires à Lévis, Québec.");
    setMeta('keywords', 'mentions légales, Couvreur-Lévis.com, légal, Québec');

    removeOldLdJson();

    const ld = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Mentions légales",
      url: "https://couvreur-levis.com/mentions-legales",
      description: "Mentions légales de Couvreur-Lévis.com, plateforme de mise en relation entre clients et couvreurs partenaires à Lévis, Québec.",
      publisher: {
        "@type": "Organization",
        name: "Couvreur-Lévis.com",
        url: "https://couvreur-levis.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Lévis",
          addressRegion: "QC",
          addressCountry: "CA",
        },
      },
      dateModified: "2026-05-16",
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'mentions-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      removeOldLdJson();
    };
  }, []);

  const navigateTo = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero compact */}
        <section className="bg-soft-gray pt-24 md:pt-32 pb-8 md:pb-12">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="mb-4 md:mb-6">
              <ol className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
                <li>
                  <a
                    href="/"
                    onClick={(e) => { e.preventDefault(); navigateTo('/'); }}
                    className="hover:text-copper transition-colors"
                  >
                    {t('breadcrumb_home')}
                  </a>
                </li>
                <li className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-gray-400" />
                </li>
                <li className="text-slate-primary font-medium">
                  {t('breadcrumb_current')}
                </li>
              </ol>
            </nav>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-manrope font-bold text-slate-primary mb-3">
              {t('hero_h1')}
            </h1>
            <p className="text-sm md:text-base text-gray-500">
              {t('hero_date')}
            </p>
          </div>
        </section>

        <FadeInSection delay={150}><ContentMentions /></FadeInSection>
      </main>
      <Footer />
    </div>
  );
}